import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useAdminGetDashboardContentDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardContentDataQuery';
import { icons } from '../../themes/icons';
import { formatNumberWithCommas } from '../../utils/converters';
import { calculatePercentageChange } from '../../utils/dashboard';
import { toLocaleString } from '../../utils/format-number';
import StatCard from './StatCard';

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  selectedLabel: string;
  marketID: string | null;
};

export const Content: React.FC<Props> = ({
  fromDate,
  priorFrom,
  priorTo,
  toDate,
  selectedLabel,
  marketID,
}) => {
  const { data, isFetching } = useAdminGetDashboardContentDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
    marketID,
  });

  const { numberPurchasedContentPercentage, totalPurchasedContentPercentage } =
    React.useMemo(() => {
      const numberPurchasedContentPercentage = calculatePercentageChange(
        data?.totalPurchasedContentCurrent || 0,
        data?.totalPurchasedContentPrior || 0,
      );

      const totalPurchasedContentPercentage = calculatePercentageChange(
        data?.totalPurchasedContentValueCurrent || 0,
        data?.totalPurchasedContentValuePrior || 0,
      );

      return { numberPurchasedContentPercentage, totalPurchasedContentPercentage };
    }, [data]);

  return (
    <BoxContainer>
      <AllStats>
        <Heading>
          <Link to={'/content'} style={{ color: 'inherit', textDecoration: 'none' }}>
            Content
          </Link>
        </Heading>
        <Time>{selectedLabel}</Time>
      </AllStats>
      <StatCard
        isFetching={isFetching}
        isDesktop
        name="Uploaded content"
        count={toLocaleString((data?.totalImageContent || 0) + (data?.totalVideoContent || 0))}
        icon={<icons.GalleryBlueIcon />}
      />
      <NestedBlock>
        <StatCard
          isFetching={isFetching}
          isDesktop
          name="Images"
          count={toLocaleString(data?.totalImageContent || 0)}
          icon={<icons.GalleryBlueIcon />}
        />
        <StatCard
          isFetching={isFetching}
          isDesktop
          name="Videos"
          count={toLocaleString(data?.totalVideoContent || 0)}
          icon={<icons.VideoBlueIcon />}
        />
      </NestedBlock>
      <StatCard
        isFetching={isFetching}
        isDesktop
        name="Number of content purchased"
        count={toLocaleString(data?.totalPurchasedContentCurrent || 0)}
        compare={`${numberPurchasedContentPercentage}%`}
        icon={<icons.GalleryBlueIcon />}
        percentageNumber={numberPurchasedContentPercentage}
      />
      <StatCard
        isDesktop
        name="Purchased content"
        count={`$${formatNumberWithCommas(data?.totalPurchasedContentValueCurrent || 0)}`}
        compare={`${totalPurchasedContentPercentage}%`}
        icon={<icons.DollarIcon />}
        rightAlignItems="flex-end"
        percentageNumber={totalPurchasedContentPercentage}
      />
    </BoxContainer>
  );
};

const BoxContainer = styled.div`
  margin-top: 20px;
  border: 1px solid #00000014;
  border-radius: 8px;
  padding: 30px;
`;
const AllStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #0000000d;
  padding-bottom: 15px;
`;

const Heading = styled.div`
  color: #111111;
  font-size: 18px;
  font-family: Poppins;
  font-weight: 500;
`;

const Time = styled.div`
  color: rgba(0, 0, 0, 0.5);
  font-size: 13px;
  font-family: Poppins;
  font-weight: 500;
`;
const NestedBlock = styled.div`
  margin-left: 40px;
`;
